import React, { useState, useRef, useEffect, useCallback } from 'react';
import './M3TabBar.css';

/**
 * M3 Tab Bar — fixed, scrollable, or auto mode.
 *
 * @param {Array<{key, label, icon?}>} tabs
 * @param {string} activeKey
 * @param {Function} onTabChange
 * @param {'fixed'|'scrollable'|'auto'} [mode='fixed']
 * @param {ReactNode} [children] — tab content
 */
export default function M3TabBar({ tabs = [], activeKey, onTabChange, mode = 'fixed', children }) {
  const scrollRef = useRef(null);

  const handleTabClick = useCallback(
    (key) => {
      if (key !== activeKey) onTabChange(key);
    },
    [activeKey, onTabChange]
  );

  const scroll = useCallback((dir) => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({ left: dir * 200, behavior: 'smooth' });
    }
  }, []);

  const showScrollArrows = mode === 'scrollable';

  return (
    <div className="m3-tab-bar-container">
      {showScrollArrows && (
        <button
          className="m3-tab-scroll-btn m3-tab-scroll-btn-left"
          onClick={() => scroll(-1)}
          aria-label="Scroll tabs left"
        >
          <span className="material-symbols-rounded">chevron_left</span>
        </button>
      )}
      <div ref={scrollRef} className={`m3-tab-bar m3-tab-bar-${mode}`} role="tablist">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            className={`m3-tab ${activeKey === tab.key ? 'm3-tab-active' : ''}`}
            role="tab"
            aria-selected={activeKey === tab.key}
            aria-controls={`panel-${tab.key}`}
            onClick={() => handleTabClick(tab.key)}
          >
            {tab.icon && <span className="m3-tab-icon">{tab.icon}</span>}
            <span className="m3-tab-label">{tab.label}</span>
          </button>
        ))}
      </div>
      {showScrollArrows && (
        <button
          className="m3-tab-scroll-btn m3-tab-scroll-btn-right"
          onClick={() => scroll(1)}
          aria-label="Scroll tabs right"
        >
          <span className="material-symbols-rounded">chevron_right</span>
        </button>
      )}
    </div>
  );
}

/**
 * Tab content panel.
 */
export function M3TabPanel({ id, active, children }) {
  if (!active) return null;
  return (
    <div id={id} role="tabpanel" className="m3-tab-panel">
      {children}
    </div>
  );
}
